import Cookies from 'js-cookie';
import API_CIFRA from './Variables_apis';

// Maneja el ciclo de vida del JWT (access + refresh) guardándolo en cookies
// (js-cookie) en vez de localStorage. Nota: son cookies puestas desde JS, sin
// `httpOnly` (eso solo lo puede setear el servidor), así que siguen siendo
// legibles por cualquier script de la página igual que localStorage — la
// diferencia real es que expiran solas (`COOKIE_OPTIONS.expires`), no que
// sean más seguras contra XSS.
// `tokenManager` de abajo es el punto único de lectura/escritura/borrado del
// token; nada más en el frontend debería tocar `Cookies` directamente.
// Configuración de autenticación
const AUTH_CONFIG = {
  // Claves para cookies
  TOKEN_KEY: 'jwt_token_cifra_negra',
  REFRESH_TOKEN_KEY: 'refresh_token_cifra_negra',
  TOKEN_EXPIRY_KEY: 'token_expiry_cifra_negra',
  USER_DATA_KEY: 'user_data_cifra_negra',
  
  // Configuración de cookies
  COOKIE_OPTIONS: {
    expires: 7, // 7 días
    secure: process.env.NODE_ENV === 'production',
    sameSite: 'strict'
  }
};

// Funciones de manejo de tokens
export const tokenManager = {
  // Guardar tokens en cookies
  setTokens: (accessToken, refreshToken, userData = null) => {
    Cookies.set(AUTH_CONFIG.TOKEN_KEY, accessToken, AUTH_CONFIG.COOKIE_OPTIONS);
    Cookies.set(AUTH_CONFIG.REFRESH_TOKEN_KEY, refreshToken, AUTH_CONFIG.COOKIE_OPTIONS);
    
    // Calcular expiración (1 hora por defecto)
    const expiry = new Date();
    expiry.setHours(expiry.getHours() + 1);
    Cookies.set(AUTH_CONFIG.TOKEN_EXPIRY_KEY, expiry.toISOString(), AUTH_CONFIG.COOKIE_OPTIONS);
    
    // Guardar datos del usuario si se proporcionan
    if (userData) {
      Cookies.set(AUTH_CONFIG.USER_DATA_KEY, JSON.stringify(userData), AUTH_CONFIG.COOKIE_OPTIONS);
    }
  },

  // Obtener token de acceso
  getAccessToken: () => {
    return Cookies.get(AUTH_CONFIG.TOKEN_KEY);
  },

  // Obtener refresh token
  getRefreshToken: () => {
    return Cookies.get(AUTH_CONFIG.REFRESH_TOKEN_KEY);
  },

  // Obtener datos del usuario
  getUserData: () => {
    const userData = Cookies.get(AUTH_CONFIG.USER_DATA_KEY);
    return userData ? JSON.parse(userData) : null;
  },

  // Verificar si el token está expirado
  isTokenExpired: () => {
    const expiry = Cookies.get(AUTH_CONFIG.TOKEN_EXPIRY_KEY);
    if (!expiry) return true;
    
    const expiryDate = new Date(expiry);
    const now = new Date();
    return now >= expiryDate;
  },

  // Verificar si está autenticado
  isAuthenticated: () => {
    const token = Cookies.get(AUTH_CONFIG.TOKEN_KEY);
    return token && !tokenManager.isTokenExpired();
  },

  // Limpiar todos los tokens
  clearTokens: () => {
    Cookies.remove(AUTH_CONFIG.TOKEN_KEY);
    Cookies.remove(AUTH_CONFIG.REFRESH_TOKEN_KEY);
    Cookies.remove(AUTH_CONFIG.TOKEN_EXPIRY_KEY);
    Cookies.remove(AUTH_CONFIG.USER_DATA_KEY);
  },

  // Actualizar solo el access token
  updateAccessToken: (newAccessToken) => {
    Cookies.set(AUTH_CONFIG.TOKEN_KEY, newAccessToken, AUTH_CONFIG.COOKIE_OPTIONS);
    
    // Actualizar expiración
    const expiry = new Date();
    expiry.setHours(expiry.getHours() + 1);
    Cookies.set(AUTH_CONFIG.TOKEN_EXPIRY_KEY, expiry.toISOString(), AUTH_CONFIG.COOKIE_OPTIONS);
  }
};

// Configuración de axios para interceptors
export const axiosConfig = {
  // Interceptor para agregar token automáticamente
  requestInterceptor: (config) => {
    const token = tokenManager.getAccessToken();
    if (token && !tokenManager.isTokenExpired()) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },

  // Interceptor para manejar errores de autenticación
  responseInterceptor: (response) => {
    return response;
  },

  // Interceptor para manejar errores
  errorInterceptor: async (error) => {
    const originalRequest = error.config;

    // Si es error 401 y no es un retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        // Intentar refresh token
        const refreshToken = tokenManager.getRefreshToken();
        if (refreshToken) {
          const response = await fetch(API_CIFRA.usuario.refreshToken, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh: refreshToken }),
          });

          if (response.ok) {
            const data = await response.json();
            tokenManager.updateAccessToken(data.access);
            
            // Reintentar la petición original
            originalRequest.headers.Authorization = `Bearer ${data.access}`;
            return fetch(originalRequest.url, originalRequest);
          }
        }
      } catch (refreshError) {
        // Si falla el refresh, limpiar tokens y redirigir
        tokenManager.clearTokens();
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
};

export default AUTH_CONFIG; 